import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import ToolCard from './ToolCard';
import ToolDrawer from './ToolDrawer';
import { toastSuccess } from '../../../lib/toast';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'mine', label: 'I have access' },
  { id: 'team', label: 'Requested by team' },
  { id: 'defaults', label: 'Department defaults' },
];

export default function ToolsTab({ deptId, onCount, onRequestSubmitted: notifyParent }) {
  const [tools, setTools] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [drawerToolId, setDrawerToolId] = useState(null);
  const activeRef = useRef(true);

  // quiet: keep the current cards while refetching. After "Request for N" the
  // drawer returns focus to the card that opened it; blanking the grid would
  // detach that card and drop focus to <body>.
  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setTools(null);
    setError('');
    try {
      const r = await axios.get(`${API_BASE_URL}/api/dept/${deptId}/catalog`, { withCredentials: true });
      const list = r.data.tools || [];
      if (!activeRef.current) return;
      setTools(list);
      if (onCount) onCount(list.length);
    } catch {
      if (activeRef.current) setError("Couldn't load the department catalog.");
    }
  }, [deptId, onCount]);

  useEffect(() => {
    activeRef.current = true;
    load();
    return () => { activeRef.current = false; };
  }, [load]);

  const filtered = (tools || []).filter((t) => {
    const q = search.trim().toLowerCase();
    if (q && !`${t.name} ${t.category_name || ''}`.toLowerCase().includes(q)) return false;
    if (filter === 'mine') return t.i_have_access;
    if (filter === 'team') return !t.is_dept_default && t.was_requested;
    if (filter === 'defaults') return t.is_dept_default;
    return true;
  });

  const onRequestSubmitted = (count) => {
    toastSuccess(`Request submitted for ${count} member${count === 1 ? '' : 's'}. Admins will review.`);
    setDrawerToolId(null);
    load({ quiet: true });
    // The header's Pending stat lives in the parent; let it re-read.
    if (notifyParent) notifyParent();
  };

  return (
    <div>
      <div className="dept2-toolbar">
        <label className="visually-hidden" htmlFor="dept2-search">Search tools</label>
        <input
          id="dept2-search"
          className="search-input"
          type="search"
          placeholder="Search tools by name or category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
        />
        <div className="dept2-pills" role="group" aria-label="Filter tools">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filter === f.id ? 'dept2-pill active' : 'dept2-pill'}
              aria-pressed={filter === f.id}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="dept2-error" role="alert">
          <p>{error}</p>
          <button type="button" className="btn btn-secondary btn-small" onClick={load}>Try again</button>
        </div>
      )}

      {!error && tools === null && (
        <ul className="dept2-grid" role="status" aria-busy="true" aria-label="Loading catalog">
          {Array.from({ length: 8 }).map((_, i) => <li key={i} className="dept2-skeleton" aria-hidden="true" />)}
        </ul>
      )}

      {!error && tools && tools.length === 0 && (
        <div className="dept2-empty"><p>No tools in the catalog yet.</p></div>
      )}

      {!error && filtered.length > 0 && (
        <ul className="dept2-grid">
          {filtered.map((t) => (
            <li key={t.id}><ToolCard tool={t} onClick={() => setDrawerToolId(t.id)} /></li>
          ))}
        </ul>
      )}

      {!error && tools && tools.length > 0 && filtered.length === 0 && (
        <div className="dept2-empty">
          <p>No tools match your filter.</p>
          <button type="button" className="btn btn-secondary btn-small" onClick={() => { setSearch(''); setFilter('all'); }}>Clear filters</button>
        </div>
      )}

      {drawerToolId && tools && (
        <ToolDrawer
          deptId={deptId}
          toolId={drawerToolId}
          tool={tools.find((t) => t.id === drawerToolId)}
          onClose={() => setDrawerToolId(null)}
          onRequestSubmitted={onRequestSubmitted}
        />
      )}
    </div>
  );
}
